import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { SaveTransitionScreenBody } from '@models/api';
import { KeyValueRoleModel } from '@models/common';
import { ListResponse } from '@models/helpers';
import { SearchViewModel } from '@models/module/grid';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { TransitionFormValue } from '@module-definition/document/common';
import { DocumentHeaderData } from '@module-definition/document/document-header/document-header.component';
import { EntityInstanceTransition } from '@module-instance/dynamic-module/common';
import {
  ModuleGriRequestParams,
  ModuleGridService,
  ModuleInstanceService,
  PresetDocument,
} from '@module-instance/services';
import { ModuleInstanceCommonService } from '@module-instance/services/module-instance-common.service';
import { TableDataService } from '@shared/components/data-list/common';
import { AccessLogRequest, AccessLogTableRow, DocumentDetailsQueryParams, DocumentInfoModel } from '..';

type TransitionParams = {
  params: DocumentDetailsQueryParams;
  transition: EntityInstanceTransition;
  data: Partial<TransitionFormValue>;
  assignee: KeyValueRoleModel<string, string>;
  workflowGroupId?: number;
  presetId?: number;
  documentNumber?: number;
};

@Injectable({ providedIn: 'root' })
export class DocumentDetailsService {
  private moduleInstanceService = inject(ModuleInstanceService);
  private moduleGridService = inject(ModuleGridService);
  private tableDataService = inject(TableDataService);
  private moduleInstanceCommonService = inject(ModuleInstanceCommonService);

  getRelations(queryParams: Pick<DocumentDetailsQueryParams, 'entityInstanceId' | 'contractId' | 'moduleId'>) {
    return this.moduleInstanceService
      .getRelations(queryParams)
      .pipe(map(({ relations }) => relations.filter(relation => relation.records > 0)));
  }

  getAccessLog({ entityInstanceId, contractId, body }: AccessLogRequest): Observable<ListResponse<AccessLogTableRow>> {
    return (
      this.moduleInstanceService
        .getAccessLog({ entityInstanceId, contractId, body })
        //   TODO: we should have the same property names for status (i would use tableService for this mapping)
        .pipe(
          map(({ data, records }) => {
            const mappedData: AccessLogTableRow[] = data.map(item => ({
              ...item,
              state_name: {
                bgColor: item.stateColor,
                color: item.stateTextColor,
                label: item.state,
              },
            }));
            return {
              data: mappedData,
              records,
            };
          }),
          catchError(() => of({ data: [], records: 0 })),
        )
    );
  }

  getTransitionFields(params: DocumentDetailsQueryParams, transition: EntityInstanceTransition) {
    const { id, roles, isComment, isEmail, isRevise } = transition;
    const transitionId = isComment || isEmail || isRevise ? null : id;
    const actionId = transitionId ? null : id;
    return this.moduleInstanceService.getFieldsEntityInstance({
      ...params,
      transitionId,
      actionId,
      roleId: roles[0]?.key,
    });
  }

  submitTransition({
    params,
    transition,
    data,
    assignee,
    workflowGroupId,
    presetId,
    documentNumber,
  }: TransitionParams): Observable<number> {
    const { contractId, workflowId, entityInstanceId, moduleId, screenId } = params;
    const { notify, includeAttachments, excludeRoles } = data?.notificationData ?? {};
    const { id, roles, isComment, isEmail, isRevise } = transition;
    const transitionId = !isComment && !isEmail && !isRevise ? id : null;
    const actionId = transitionId ? null : id;
    const baseParams = { contractId, workflowId, entityInstanceId, moduleId, roleId: roles[0]?.key };
    const groups: string[] = [];
    const emails: string[] = [];
    notify?.forEach(n => {
      if (n.includes('@')) {
        emails.push(n);
      } else {
        groups.push(n);
      }
    });
    const body: Partial<SaveTransitionScreenBody> = {
      emails,
      groups,
      screenId,
      includeAttachments,
      documentNumber,
      roleToExcludeIds: excludeRoles,
      manualAssignUser: assignee,
      values: data?.values,
      workflowGroupId,
    };
    if (presetId) {
      return this.moduleInstanceService.saveTemplateInstance({ ...baseParams, transitionId, presetId, body });
    }

    if (isComment) {
      return this.moduleInstanceService.addComment({ ...baseParams, actionId }, body).pipe(map(() => null));
    }

    if (isEmail) {
      return this.moduleInstanceService.sendActionEmail({ ...baseParams, actionId }, body).pipe(map(() => null));
    }

    return this.moduleInstanceCommonService.saveTransitionScreen({ ...baseParams, transitionId }, body);
  }

  gridSidebarSearch({ body, contractId, moduleId }: ModuleGriRequestParams<SearchViewModel>) {
    return this.moduleGridService.gridSidebarSearch({ body, contractId, moduleId }).pipe(
      map(({ data, ...rest }) => {
        return {
          ...rest,
          data: this.tableDataService.mapTableRowsState(data),
        };
      }),
    );
  }

  /** Data used to control the document header */
  getHeaderData({
    moduleId,
    presetData,
    info,
  }: {
    moduleId: number;
    presetData?: PresetDocument;
    info?: DocumentInfoModel;
  }): DocumentHeaderData {
    if (presetData) {
      return {
        moduleId,
        preset: {
          name: presetData.name,
          description: presetData.description,
        },
      };
    }
    const { state, stateColor, stateTextColor, contractName, contractNumber, ballInCourt, entityInstanceId } =
      info || {};
    /* For Contracts, Programs and Projects modules, only return state information */
    if (
      moduleId === SystemModulesEnum.Contracts ||
      moduleId === SystemModulesEnum.Programs ||
      moduleId === SystemModulesEnum.Projects
    ) {
      return {
        moduleId,
        entityInstanceId,
        state,
        stateColor,
        stateTextColor,
      };
    }
    return {
      entityInstanceId,
      moduleId,
      contractName,
      contractNumber,
      ballInCourt,
      state,
      stateColor,
      stateTextColor,
    };
  }
}
